import React, { useCallback, useEffect, useMemo, useState } from "react";
import { IoCheckbox } from "react-icons/io5";
import CustomSelect from "../../../CustomSelect";

import {
  useAdminUsersId,
  useGetPrivilegesByUser,
  useGetRoles,
  useGetUserRef,
  usePages,
} from "../../../../sevices/hooks/admin";
import _ from "lodash";
import { RiLoader2Fill } from "react-icons/ri";
import OTPBox from "../OtpBox";
import { ImSpinner6 } from "react-icons/im";
import { usePermissions } from "../../../../sevices/permissions";
import { useLocation } from "react-router-dom";

export type IOptionType = {
  id: any;
  label: string;
  slug: string;
} | null;

interface CheckedItems {
  appAccesses: { [key: string]: boolean };
  permissions: Permissions;
}

interface PermissionObject {
  create: boolean;
  read: boolean;
  edit: boolean;
  delete: boolean;
  approve: boolean;
  download: boolean;
}

interface Permissions {
  [key: string]: PermissionObject;
}

type PermissionKey =
  | "read"
  | "create"
  | "edit"
  | "delete"
  | "approve"
  | "download";
type PermissionMap = Record<PermissionKey, number>;
type PermissionState = Record<PermissionKey, boolean>;

interface FormattedPermissions {
  roleId: string;
  pagePermissions: Array<{
    pageId: string;
    permissions: number[];
  }>;
}

const permissionMap: PermissionMap = {
  read: 1,
  create: 2,
  edit: 4,
  delete: 8,
  approve: 16,
  download: 32,
};
const generatePermissions = (pagesData: any) => {
  console.log(pagesData, "pagesData");
  const pages = Array.isArray(pagesData) ? pagesData : [];

  const basePermissions = [
    {
      id: 0,
      label: "All",
      value: "all",
      enabled: false,
    },
    {
      id: 1,
      label: "Read",
      value: "read",
      enabled: false,
    },
    {
      id: 2,
      label: "Create",
      value: "create",
      enabled: false,
    },
    {
      id: 4,
      label: "Edit",
      value: "edit",
      enabled: false,
    },
    {
      id: 8,
      label: "Delete",
      value: "delete",
      enabled: false,
    },
    {
      id: 16,
      label: "Approve",
      value: "approve",
      enabled: false,
    },
    {
      id: 32,
      label: "Download",
      value: "download",
      enabled: false,
    },
  ];

  // All Features template
  const allFeaturesTemplate = {
    id: "all-features",
    label: "All Features",
    value: "all_features",
    permissions: [...basePermissions],
  };

  // Map the pages data to the required format
  const mappedPages = pages.map((page) => ({
    id: page.id,
    label: page.name,
    value: page.key,
    permissions: [...basePermissions],
  }));

  // Return array with All Features first, followed by mapped pages
  return [allFeaturesTemplate, ...mappedPages];
};
const getPermissionIds = (permissions: Record<string, boolean>): number[] => {
  return (Object.entries(permissions) as [PermissionKey, boolean][])
    .filter(([_, value]) => value === true)
    .map(([key]) => permissionMap[key])
    .filter((id): id is number => id !== undefined);
};

const AccessPermission = () => {
  const location = useLocation();
  const { setFormattedPermissions: setFormattedPermission } = usePermissions();
  const [isPageLoading, setIsPageLoading] = useState(true);
  const searchParams = new URLSearchParams(location.search);
  const urlUserId = searchParams.get("userId");
  const [isRefCodeLoading, setIsRefCodeLoading] = useState(false);
  const [role, setRole] = useState<IOptionType>(null);
  const [refCode, setRefCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [typedText, setTypedText] = useState("");
  const [textIndex, setTextIndex] = useState(0);
  const [showRole, setShowRole] = useState(false);
  const [userId, setUserId] = useState("");
  // const { data: allRoles } = useGetRoles();
  // const { data: allPages, isLoading } = usePages();
  // const { data: userById } = useAdminUsersId(urlUserId as string);
  const {
    data: userRef,
    refetch,
    isError,
    error,
    isLoading: isUserLoading,
  } = useGetUserRef(refCode);
  // const { data: user } = useGetPrivilegesByUser(urlUserId);

 const effectiveUserId = useMemo(() => {
   return urlUserId || userRef?.data?.id;
 }, [urlUserId, userRef?.data?.id]);

  const { data: userById, isLoading: isUserByIdLoading } = useAdminUsersId(
    effectiveUserId as string
  );
  const { data: user, isLoading: isPrivilegesLoading } =
    useGetPrivilegesByUser(effectiveUserId);
  const { data: allRoles, isLoading: isRolesLoading } = useGetRoles();
  const { data: allPages, isLoading: isPagesLoading } = usePages();

  useEffect(() => {
    if (refCode) {
      setIsRefCodeLoading(true);
    }
  }, [refCode]);
  useEffect(() => {
    if (refCode && userRef?.data?.id && !isUserLoading) {
      setIsRefCodeLoading(false);
      setUserId(userRef.data.id);
    }
  }, [refCode, userRef, isUserLoading]);
  const transformedRoles = useMemo(
    () =>
      allRoles?.data?.map(({ code, id, name }: any) => ({
        id,
        label: name,
        slug: code.toLowerCase().replace(/\s+/g, "-"),
      })) || [],
    [allRoles?.data]
  );
  const generateAppAccessWithPermissions = useMemo(
    () => generatePermissions(allPages?.data),
    [allPages?.data]
  );
  const [checkedItems, setCheckedItems] = useState<CheckedItems>({
    appAccesses: {},
    permissions: {},
  });
  const userFullName = useMemo(() => {
    if (urlUserId && userById?.data?.data) {
      const { firstName, lastName } = userById.data.data;
      return `${firstName} ${lastName}`;
    }
    return "";
  }, [urlUserId, userById]);
  useEffect(() => {
    if (
      urlUserId &&
      user?.data &&
      Array.isArray(user.data) &&
      user.data.length > 0
    ) {
      // Pre-fill role selection
      const userRole = user.data[0]?.roleId; // Get roleId from first item since it's same for all
      const matchingRole = transformedRoles.find(
        (role: { id: any }) => role.id === userRole
      );
      if (matchingRole) {
        setRole(matchingRole);
      }

      // Pre-fill checkboxes and permissions
      const newCheckedItems: CheckedItems = {
        appAccesses: {},
        permissions: {},
      };

      // Process each page in user privileges
      user.data.forEach((privilege: { pageId: any; permissions: any[] }) => {
        // Find corresponding page in generateAppAccessWithPermissions
        const page = generateAppAccessWithPermissions.find(
          (p) => p.id === privilege.pageId
        );
        if (page) {
          // Mark page as accessed
          newCheckedItems.appAccesses[page.value] = true;

          // Set permissions based on the permissions array
          const permissionState: PermissionObject = {
            create: false,
            read: false,
            edit: false,
            delete: false,
            approve: false,
            download: false,
          };

          // Convert permission numbers to boolean states
          privilege.permissions.forEach((permValue) => {
            if (permValue === 1) permissionState.read = true;
            if (permValue === 2) permissionState.create = true;
            if (permValue === 4) permissionState.edit = true;
            if (permValue === 8) permissionState.delete = true;
            if (permValue === 16) permissionState.approve = true;
            if (permValue === 32) permissionState.download = true;
          });

          newCheckedItems.permissions[page.value] = permissionState;
        }
      });

      setCheckedItems(newCheckedItems);
    }
  }, [urlUserId, user, transformedRoles, generateAppAccessWithPermissions]);
  console.log(userId, "userId");

  const [filteredPermissions, setFilteredPermissions] = useState<Permissions>(
    {}
  );
  const [formattedPermissions, setFormattedPermissions] =
    useState<FormattedPermissions>({
      roleId: "",
      pagePermissions: [],
    });
  console.log(allRoles?.data, "allRoles");
  console.log(allPages?.data, "allPages");

 const isDataLoading = useMemo(() => {
   if (urlUserId) {
     return (
       isPagesLoading ||
       isRolesLoading ||
       isPrivilegesLoading ||
       isUserByIdLoading
     );
   } else if (refCode) {
     return (
       isPagesLoading ||
       isRolesLoading ||
       isPrivilegesLoading ||
       isUserLoading ||
       isRefCodeLoading
     );
   }
   return isPagesLoading || isRolesLoading;
 }, [
   urlUserId,
   refCode,
   isPagesLoading,
   isRolesLoading,
   isPrivilegesLoading,
   isUserByIdLoading,
   isUserLoading,
   isRefCodeLoading,
 ]);

 useEffect(() => {
   if (!isDataLoading) {
     setIsPageLoading(false);
   }
 }, [isDataLoading]);

  useEffect(() => {
    if (!role || !checkedItems.appAccesses) return;

    const pagePermissions = Object.entries(checkedItems.permissions)
      .filter(([pageValue]) => {
        // Skip all_features entry and only include checked pages
        return (
          pageValue !== "all_features" && checkedItems.appAccesses[pageValue]
        );
      })
      .map(([pageValue, permissions]) => {
        // Find the corresponding page from generateAppAccessWithPermissions
        const page = generateAppAccessWithPermissions.find(
          (p) => p.value === pageValue
        );
        if (!page) return null;

        return {
          pageId: page.id,
          permissions: getPermissionIds(
            permissions as Record<PermissionKey, boolean>
          ),
        };
      })
      .filter(
        (item): item is NonNullable<typeof item> =>
          item !== null && item.permissions.length > 0
      );

    const formatted: FormattedPermissions = {
      roleId: role.id,
      pagePermissions,
    };

    setFormattedPermissions(formatted);
    setFormattedPermission(formatted);
    console.log("Formatted Permissions:", formatted);
  }, [role, checkedItems, generateAppAccessWithPermissions]);

  console.log(formattedPermissions, ":formattedPermissions");

  console.log(
    generateAppAccessWithPermissions,
    "generateAppAccessWithPermissions"
  );

 const handleRef = (code: string) => {
   setRefCode(code);
   setIsRefCodeLoading(true);
 };

  const handleAppAccessChange = (value: string, isChecked: boolean) => {
    setCheckedItems((prevState) => {
      if (value === "all_features") {
        // Get all pages except "all_features"
        const pages = generateAppAccessWithPermissions.filter(
          (item) => item.value !== "all_features"
        );

        const newPermissions = pages.reduce<Record<string, PermissionState>>(
          (acc, page) => {
            acc[page.value] = {
              create: isChecked,
              read: isChecked,
              edit: isChecked,
              delete: isChecked,
              approve: isChecked,
              download: isChecked,
            };
            return acc;
          },
          {}
        );

        const newAppAccesses = pages.reduce<Record<string, boolean>>(
          (acc, page) => {
            acc[page.value] = isChecked;
            return acc;
          },
          {}
        );

        return {
          appAccesses: newAppAccesses,
          permissions: newPermissions,
        };
      }

      const newState = {
        appAccesses: {
          ...prevState.appAccesses,
          [value]: isChecked,
        },
        permissions: {
          ...prevState.permissions,
        },
      };

      if (isChecked) {
        newState.permissions[value] = {
          create: false,
          read: false,
          edit: false,
          delete: false,
          approve: false,
          download: false,
        };
      } else {
        delete newState.permissions[value];
      }

      return newState;
    });
  };

  console.log(userRef?.data, "userRef");

  const handlePermissionChange = (
    appAccessValue: string,
    permissionValue: string,
    isChecked: boolean
  ) => {
    setCheckedItems((prevState) => {
      const currentPermissions = prevState.permissions[appAccessValue] || {
        create: false,
        read: false,
        edit: false,
        delete: false,
        approve: false,
        download: false,
      };

      let newPermissions: PermissionState;

      if (permissionValue === "all") {
        // If "All" is clicked, set all permissions to the same state
        newPermissions = {
          create: isChecked,
          read: isChecked,
          edit: isChecked,
          delete: isChecked,
          approve: isChecked,
          download: isChecked,
        };
      } else {
        // For individual permissions
        newPermissions = {
          ...currentPermissions,
          [permissionValue]: isChecked,
        };
      }

      return {
        ...prevState,
        permissions: {
          ...prevState.permissions,
          [appAccessValue]: newPermissions,
        },
      };
    });
  };

  useEffect(() => {
    const resetStates = () => {
      setRole(null);
      setRefCode("");
      setLoading(false);
      setTypedText("");
      setTextIndex(0);
      setShowRole(false);
      setUserId("");
      setCheckedItems({
        appAccesses: {},
        permissions: {},
      });
      setFilteredPermissions({});
      setFormattedPermissions({
        roleId: "",
        pagePermissions: [],
      });
      setFormattedPermission({
        roleId: "",
        pagePermissions: [],
      });
    };

    resetStates();
  }, [location.pathname, setFormattedPermission]);

  useEffect(() => {
    const { appAccesses, permissions } = checkedItems;

    if (Object.keys(appAccesses).length > 0) {
      const appAccessKeys = Object.keys(appAccesses);

      const filtered = Object.entries(permissions)
        .filter(
          ([key]) => appAccessKeys.includes(key) && key !== "all_features"
        )
        .reduce((obj, [key, value]) => {
          //@ts-ignore
          obj[key] = value;
          return obj;
        }, {});

      setFilteredPermissions(filtered);
    } else {
      setFilteredPermissions({});
    }
    setUserId(userRef?.data.id);
  }, [checkedItems]);

  console.log(role, "ju");

  useEffect(() => {
    if (userRef?.data?.id) {
      setUserId(userRef.data.id);

      // Start typing animation when we get user data
      const data = userRef.data;
      const fullName = `${data.lastName} ${data.firstName}`;
      setTypedText("");
      setTextIndex(0);

      const interval = setInterval(() => {
        setTypedText((prev) => {
          const next = fullName.slice(0, prev.length + 1);
          if (next === fullName) {
            clearInterval(interval);
            setShowRole(true);
          }
          return next;
        });
      }, 100);

      return () => clearInterval(interval);
    }
  }, [userRef?.data]);

  const userRole = userRef?.data?.role?.name;
  // const disableCheckboxes = !isLoading && (!userRef || !userRef?.data);

  // useEffect(() => {
  //   if (isError) {
  //     toast.error("Something went wrong");
  //   }
  // }, [isError]);

  const isAllFeaturesChecked = useCallback(() => {
    if (!checkedItems.appAccesses) return false;

    const relevantPages = generateAppAccessWithPermissions.filter(
      (item) => item.value !== "all_features"
    );

    return relevantPages.every(
      (page) => checkedItems.appAccesses[page.value] === true
    );
  }, [checkedItems.appAccesses, generateAppAccessWithPermissions]);

  // if (isPageLoading) {
  //   return (
  //     <div className="w-full h-screen">
  //       <div className="animate-pulse space-y-4 p-4">
  //         {/* Header skeleton */}
  //         <div className="h-8 bg-gray-200 rounded w-1/4"></div>
  //         <div className="h-4 bg-gray-200 rounded w-2/3"></div>

  //         {/* Role selector skeleton */}
  //         <div className="mt-10 h-10 bg-gray-200 rounded w-1/3"></div>

  //         {/* Permissions grid skeleton */}
  //         <div className="mt-6">
  //           {[1, 2, 3, 4, 5].map((i) => (
  //             <div
  //               key={i}
  //               className="mt-4 h-16 bg-gray-200 rounded w-full"
  //             ></div>
  //           ))}
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }


   if (isPageLoading || isRefCodeLoading) {
     return <SkeletonLoader />;
   }

  return (
    <div>
      <div>
        <h1 className="text-[20px] font-semibold text-[#071827]">
          {urlUserId ? "Edit Access Permissions" : "Assign Access Permissions"}
        </h1>
        <p className="text-[#5C6F7F] text-[16px] font-light leading-normal mt-3">
          {urlUserId ? (
            <span>
              Modify the access permissions for{" "}
              <span className="font-bold">{userFullName}</span>
            </span>
          ) : (
            "Select features you wish to grant your user access to"
          )}
        </p>
      </div>
      <div className="mt-10">
        {!urlUserId && (
          <div className="flex items-center justify between w-full ">
            <div
              className={`flex w-full items-center ${
                !isDataLoading ? "gap-10" : "justify-between"
              } `}
            >
              <div className="relative w-[40%] ">
                <span className="ml-2 ">Enter Users Invitation's Code</span>
                <OTPBox onOTPComplete={handleRef} />
              </div>
              <div className="">
                {isDataLoading ? (
                  <div className="text-center flex items-center ">
                    <RiLoader2Fill
                      size={30}
                      fill="#0eb683"
                      className="animate-spin"
                    />
                    <RiLoader2Fill
                      size={30}
                      fill="#0eb683"
                      className="animate-spin"
                    />
                    <RiLoader2Fill
                      size={30}
                      fill="#0eb683"
                      className="animate-spin"
                    />
                    <RiLoader2Fill
                      size={30}
                      fill="#0eb683"
                      className="animate-spin"
                    />
                  </div>
                ) : (
                  <div>
                    {userRef && (
                      <div>
                        <p className="text-2xl font-bold text-[#0eb683]">
                          {typedText}
                        </p>
                        {showRole && (
                          <div className="flex items-center gap-3">
                            <p className="text-[#5C6F7F] font-semibold">
                              Current Role:
                            </p>
                            <div className="flex items-center text-[#0eb683] gap-2">
                              {/* <span>{getIconForRole(userRole)}</span> */}
                              <span>{_.startCase(userRole)}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div>
              <div>{loading && <ImSpinner6 />}</div>
            </div>
          </div>
        )}
        {isDataLoading ? (
          <div className="relative w-[35%] mt-5">
            <div className="h-10 bg-gray-200 rounded animate-pulse"></div>
          </div>
        ) : (
          <div className="relative w-[35%] mt-5">
            <CustomSelect
              selectedOption={role}
              setSelectOption={setRole}
              placeholder={"Select Role"}
              options={transformedRoles || []}
            />
            <span className="text-red-600 absolute left-2 top-2">*</span>
          </div>
        )}
        {isDataLoading ? (
          <SkeletonLoader />
        ) : (
          <div>
            {generateAppAccessWithPermissions?.map((access) => (
              <div
                key={access.id}
                className="mt-6 border w-full h-16 flex items-center rounded border-[#0eb683] bg-[#F2FAF7]"
              >
                <div className="w-[20%]">
                  <Checkbox
                    label={access.label}
                    value={access.value}
                    name={access.value}
                    onChange={handleAppAccessChange}
                    checked={
                      access.value === "all_features"
                        ? isAllFeaturesChecked()
                        : checkedItems.appAccesses[access.value] || false
                    }
                    disabled={!role} // Disable if no role is selected
                  />
                </div>
                <div className="bg-white mr-4 flex items-center justify-between rounded-2xl w-full">
                  {access.permissions.map((permission) => (
                    <ChildCheckbox
                      key={`${access.value}.${permission.value}`}
                      label={permission.label}
                      value={`${access.value}.${permission.value}`}
                      name={permission.value}
                      parentValue={access.value}
                      onChange={(value, isChecked) =>
                        handlePermissionChange(
                          access.value,
                          permission.value,
                          isChecked
                        )
                      }
                      checked={
                        //@ts-ignore
                        checkedItems.permissions[access.value]?.[
                          permission.value
                        ] || false
                      }
                      disabled={
                        !role || !checkedItems.appAccesses[access.value]
                      } // Disable if no role or parent not checked
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const Checkbox: React.FC<CheckboxProps> = ({
  label,
  value,
  name,
  disabled,
  onChange = () => {},
  checked,
}) => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(value, event.target.checked);
  };

  return (
    <div className="w-fit px-3 pt-2 pb-1">
      <label
        className={`inline-flex items-center ${
          disabled ? "cursor-not-allowed" : "cursor-pointer"
        }`}
      >
        <input
          type="checkbox"
          className="hidden"
          checked={checked}
          onChange={handleChange}
          value={value}
          name={name}
          disabled={disabled}
        />
        <span className="relative flex items-center gap-2">
          <span
            className={`flex items-center justify-center h-5 w-5 rounded-[4px] border
              ${
                checked && !disabled
                  ? "border-white"
                  : "bg-white border-[#E2E9F0]"
              }
              ${disabled ? "bg-gray-200" : ""}`}
          >
            {checked && !disabled && (
              <div className="absolute">
                <IoCheckbox color="#0EB683" size={23} />
              </div>
            )}
          </span>
          <span
            className={`text-[#071827] text-[16px] leading-normal whitespace-nowrap ${
              disabled ? "text-gray-400" : ""
            }`}
          >
            {label}
          </span>
        </span>
      </label>
    </div>
  );
};
const ChildCheckbox: React.FC<CheckboxProps & { parentValue?: string }> = ({
  label,
  value,
  name,
  parentValue,
  disabled,
  onChange = () => {},
  checked,
}) => {
  return (
    <div className="w-fit px-3 pt-2 pb-1">
      <label
        className={`inline-flex items-center ${
          disabled ? "cursor-not-allowed" : "cursor-pointer"
        }`}
      >
        <input
          type="checkbox"
          className="hidden"
          checked={checked}
          onChange={(e) => onChange(value, e.target.checked)}
          value={value}
          name={name}
          disabled={disabled}
        />
        <span className="relative flex items-center gap-2">
          <span
            className={`flex items-center justify-center h-5 w-5 rounded-[4px] border
              ${
                checked && !disabled
                  ? "border-white"
                  : "bg-white border-[#E2E9F0]"
              }
              ${disabled ? "bg-gray-200" : ""}`}
          >
            {checked && !disabled && (
              <div className="absolute">
                <IoCheckbox color="#0EB683" size={23} />
              </div>
            )}
          </span>
          <span
            className={`text-gray-500 text-xs ${
              disabled ? "text-gray-400" : ""
            }`}
          >
            {label}
          </span>
        </span>
      </label>
    </div>
  );
};
export default AccessPermission;

export interface CheckboxProps {
  label: string;
  value: string;
  name: string;
  defaultChecked?: boolean;
  bg?: string;
  onChange?: (value: string, checked: boolean) => void;
  disabled?: boolean;
  checked?: boolean;
}

export const permissions = [
  {
    id: 0,
    label: "All",
    value: "all",
    enabled: true,
  },
  {
    id: 1,
    label: "Create",
    value: "create",
    enabled: true,
  },
  {
    id: 2,
    label: "Read",
    value: "read",
    enabled: true,
  },
  {
    id: 3,
    label: "Edit",
    value: "edit",
    enabled: true,
  },
  {
    id: 4,
    label: "Delete",
    value: "delete",
    enabled: true,
  },
  {
    id: 5,
    label: "Approve",
    value: "approve",
    enabled: true,
  },
  {
    id: 6,
    label: "Download",
    value: "download",
    enabled: true,
  },
];

const SkeletonLoader = () => {
  const features = [
    "All Features",
    "Dashboard",
    "Balances",
    "OTP",
    "Rates",
    "Settings",
    "Approvals",
    "Transactions",
    "Revenue",
    "Payouts",
  ];

  const permissions = [
    "All",
    "Read",
    "Create",
    "Edit",
    "Delete",
    "Approve",
    "Download",
  ];

  return (
    <div className="w-full  space-y-3 mt-6 animate-pulse">
      {features.map((feature) => (
        <div
          key={feature}
          className="grid grid-cols-8 gap-4 p-5 bg-gray-50 rounded-lg items-center border-2 border-gray-200"
        >
          <div className="col-span-1 flex items-center gap-2">
            <div className="w-4 h-4 rounded border-2 border-gray-200 bg-gray-100"></div>
            <div className="h-3 bg-gray-200 rounded w-24"></div>
          </div>
          {permissions.map((permission) => (
            <div key={permission} className="flex justify-center items-center ">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded border-2 border-gray-200 bg-gray-100"></div>
                <div className="h-3 bg-gray-200 rounded w-12"></div>
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};
